import Info from '@mui/icons-material/Info';
import CircularProgress from '@mui/material/CircularProgress';
import Rating from '@mui/material/Rating';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useTheme from '@mui/material/styles/useTheme';
import { useEffect, useState } from 'react';
import Iconify from '../../components/iconify/component';
import { type IconifyProps } from '../../components/iconify/types';
import { type CustomTheme } from '../../provider/ThemeProvider';
import { checkUndefined, checkUndefinedNullOrEmpty, checkUndefinedOrNull } from '../Tools';
import InputNonEditValue from './helpers/render-non-edit-value';

export type InputRatingProps = {
    autoWidth?: boolean;
    blink?: boolean;
    callback?: (params: { id: string; val: number }) => void;
    className?: string;
    disabled?: boolean;
    edit?: boolean;
    endIcon?: IconifyProps;
    error?: string;
    fatLabel?: boolean;
    helperText?: string;
    hideLabel?: boolean;
    hideNumber?: boolean;
    icon?: IconifyProps;
    id?: string;
    info?: string;
    l?: string;
    loading?: boolean;
    max?: number;
    size?: 'small' | 'medium' | 'large';
    step?: number;
    testid?: string;
    type?: 'rating';
    v?: number;
    vNew?: number;
};

const InputRating = (moduleProps: InputRatingProps) => {
    const { autoWidth, blink, callback, disabled, edit, endIcon, error, fatLabel, helperText, hideLabel, hideNumber, icon, id, info, l, loading, max, size, step, testid, v, vNew }: InputRatingProps =
        {
            autoWidth: false,
            blink: false,
            disabled: false,
            edit: false,
            fatLabel: false,
            hideLabel: false,
            hideNumber: false,
            loading: false,
            max: 5,
            size: 'medium',
            step: 1,
            ...moduleProps,
        };
    const [value, setValue] = useState<InputRatingProps['v']>(v);
    const [validate, setValidate] = useState<string>(undefined);
    const theme: CustomTheme = useTheme();

    const getValue = (val: number) => {
        if (!edit) {
            if (!loading) {
                return !checkUndefinedNullOrEmpty(val) && !isNaN(val) ? v : 0;
            }
        }

        return !checkUndefinedNullOrEmpty(val) ? val : 0;
    };

    const handleSubmit = (val: number) => {
        if (callback) callback({ id: id, val: val });
        else console.error('Callback-Funktion fehlt!\nInput-Key: inputRating_' + id);
    };

    const handleChange = (e) => {
        const newValue = !checkUndefinedNullOrEmpty(e.target.value) ? parseFloat(e.target.value) : null;

        if (!checkUndefinedNullOrEmpty(max) && newValue > max) setValidate('Der Wert ist zu groß!');
        else setValidate(undefined);

        setValue(!checkUndefinedNullOrEmpty(newValue) ? newValue : undefined);
        handleSubmit(newValue);
    };

    const countDecimals = (val: number) => {
        if (!checkUndefinedNullOrEmpty(val) && Math.floor(val) !== val) {
            return val.toString().split('.')[1].length || 0;
        }
        return 0;
    };

    const heading = [];
    if (!hideLabel) {
        if (icon) {
            heading.push(
                <span key={'inputRating_' + id + '_headingIcon'} className="materialIcon iconMarginRight">
                    <Iconify icon={icon} />
                </span>
            );
        }
        if (l) heading.push(l);
    }

    useEffect(
        function () {
            setValue(!checkUndefined(vNew) ? vNew : v);
        },
        [v, vNew]
    );

    useEffect(
        function () {
            if (blink) {
                setTimeout(() => {
                    const element = document.getElementById(id);
                    if (!checkUndefinedOrNull(element)) {
                        element.scrollIntoView({ block: 'center' });
                    }
                }, 500);
            }
        },
        [blink, id]
    );

    return edit ? (
        <div
            className={[blink ? 'blinkInputfield' : '', 'inputRating'].join(' ')}
            key={'inputRating_' + id}
            style={{ width: !autoWidth ? '100%' : undefined, marginBottom: !checkUndefinedOrNull(error) || !checkUndefinedOrNull(helperText) ? undefined : 10 }}
        >
            {(!checkUndefinedNullOrEmpty(l) || loading || icon || !checkUndefinedOrNull(endIcon) || !checkUndefinedOrNull(info)) && (
                <Typography
                    variant="subtitle1"
                    style={{ display: 'flex', alignItems: 'center', color: (!loading && !checkUndefinedOrNull(error)) || !checkUndefinedOrNull(validate) ? theme?.palette?.error?.main : undefined }}
                >
                    {(loading || icon) && (
                        <div className="materialIcon iconMarginRight" style={{ marginRight: 8 }}>
                            {loading ? <CircularProgress color="primary" size="1.5rem" /> : icon ? <Iconify icon={icon} /> : undefined}
                        </div>
                    )}
                    {l}
                    {!checkUndefinedOrNull(endIcon) && (
                        <div className="materialIcon" style={{ display: 'flex', alignItems: 'center', marginLeft: 8 }}>
                            <Iconify icon={endIcon} />
                        </div>
                    )}
                    {!checkUndefinedOrNull(info) && (
                        <div className="materialIcon" style={{ marginLeft: 8 }}>
                            <Tooltip title={info}>
                                <span>
                                    <Info color="primary" />
                                </span>
                            </Tooltip>
                        </div>
                    )}
                </Typography>
            )}
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <Rating
                    size={size}
                    disabled={loading || disabled}
                    precision={step}
                    max={max}
                    style={{ marginRight: !hideNumber ? 8 : undefined }}
                    value={!checkUndefined(value) ? getValue(value) : null}
                    onChange={handleChange}
                />
                {!hideNumber && getValue(value).toLocaleString('de-DE', { minimumFractionDigits: countDecimals(step), maximumFractionDigits: countDecimals(step) })}
            </div>
            {(!checkUndefinedOrNull(error) || !checkUndefinedOrNull(helperText)) && !loading && (
                <Typography
                    key={'inputRating' + (testid ? testid : id) + '_helperText'}
                    id={id + '_helperText'}
                    variant="body2"
                    style={{ fontSize: '0.75rem', marginTop: 4, color: !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : theme?.palette?.text?.secondary }}
                >
                    {!checkUndefinedOrNull(error) ? error : helperText}
                </Typography>
            )}
        </div>
    ) : (
        <InputNonEditValue
            autoWidth={autoWidth}
            endIcon={endIcon}
            fatLabel={fatLabel}
            finalId={!checkUndefinedNullOrEmpty(testid) ? testid : id}
            hideLabel={hideLabel}
            icon={icon}
            info={info}
            l={l}
            loading={loading}
            value={
                <Stack direction="row" gap={1}>
                    <Rating size={size} readOnly precision={step} max={max} value={!checkUndefined(v) ? getValue(v) : null} />
                    {!hideNumber && getValue(v).toLocaleString('de-DE', { minimumFractionDigits: countDecimals(step), maximumFractionDigits: countDecimals(step) })}
                </Stack>
            }
        />
    );
};

export default InputRating;
